import React, { useEffect, useState } from 'react';
import { Box, Modal, IconButton, Paper, Stack } from '@mui/material';
import Close from '@mui/icons-material/Close';
import Validator from 'validator';
import { TextView, Button, TextField } from '@atoms';
import { callUpdateUser } from '@api';

interface UploadUserProps {
  open: boolean;
  user?: User;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

interface FormValues {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  website: string;
  address: string;
}

interface FormErrors {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  website: string;
  address: string;
}

const emptyValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  companyName: '',
  website: '',
  address: ''
};

const emptyErrors: FormErrors = {
  name: '',
  email: '',
  phone: '',
  companyName: '',
  website: '',
  address: ''
};

const EditAccountModal: React.FC<UploadUserProps> = ({
  onClose,
  open,
  onSuccess,
  user
}: UploadUserProps) => {
  const [values, setValues] = useState<FormValues>(emptyValues);
  const [errors, setErrors] = useState<FormErrors>(emptyErrors);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (user) {
      setValues({
        name: user.name ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        companyName: user.company?.name ?? '',
        website: user.company?.website ?? '',
        address: user.company?.address ?? ''
      });
      setErrors(emptyErrors);
      setSubmitError(undefined);
    }
  }, [user]);

  const handleChange = (prop: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues((prev) => ({ ...prev, [prop]: event.target.value }));
  };

  const validateFields = (): boolean => {
    const next: FormErrors = { ...emptyErrors };
    let hasError = false;

    if (Validator.isEmpty(values.name)) {
      next.name = 'Name is required';
      hasError = true;
    }
    if (!Validator.isEmail(values.email)) {
      next.email = 'Enter a valid email address';
      hasError = true;
    }
    if (!Validator.isMobilePhone(values.phone)) {
      next.phone = 'Enter a valid phone number';
      hasError = true;
    }
    if (Validator.isEmpty(values.companyName)) {
      next.companyName = 'Company name is required';
      hasError = true;
    }
    if (Validator.isEmpty(values.website) || !Validator.isURL(values.website)) {
      next.website = 'Enter a valid website URL';
      hasError = true;
    }
    if (Validator.isEmpty(values.address)) {
      next.address = 'Address is required';
      hasError = true;
    }

    setErrors(next);
    return hasError;
  };

  const onSubmit = async () => {
    if (!user) return;
    if (validateFields()) return;

    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const res = await callUpdateUser(user.id, {
        name: values.name,
        phone: values.phone,
        company: {
          name: values.companyName,
          website: values.website,
          address: values.address
        }
      });
      if (res.code === 200 && res.data) {
        onSuccess(res.data);
        onClose();
      } else {
        setSubmitError(res.error?.message ?? 'Failed to update account. Please try again.');
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to update account. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-end', sm: 'center' },
        justifyContent: 'center',
        p: { xs: 0, sm: 2 }
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 720,
          maxHeight: { xs: '95vh', sm: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '12px 12px 0 0', sm: 2 },
          outline: 'none'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <TextView $fontSize={20} $fontWeight={600}>
            Edit account details
          </TextView>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ px: 3, py: 2, overflowY: 'auto', flex: 1 }}>
          <Stack spacing={3}>
            <Stack spacing={1.5}>
              <TextView $fontSize={15} $fontWeight={600} $fontColor="#475569">
                Personal details
              </TextView>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={'Name'}
                  name={'name'}
                  placeholder={'Name'}
                  errorMessage={errors.name}
                  fullWidth
                  value={values.name}
                  onChange={handleChange('name')}
                />
                <TextField
                  label={'Business Email'}
                  name={'email'}
                  placeholder={'Business Email'}
                  errorMessage={errors.email}
                  fullWidth
                  disabled
                  value={values.email}
                  onChange={handleChange('email')}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={'Phone'}
                  name={'phone'}
                  placeholder={'Phone'}
                  fullWidth
                  errorMessage={errors.phone}
                  value={values.phone}
                  onChange={handleChange('phone')}
                />
                {/* keep the right column visually balanced on md+ */}
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <TextView $fontSize={15} $fontWeight={600} $fontColor="#475569">
                Company details
              </TextView>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={'Company name'}
                  name={'company_name'}
                  placeholder={'Company name'}
                  fullWidth
                  value={values.companyName}
                  errorMessage={errors.companyName}
                  onChange={handleChange('companyName')}
                />
                {/* keep the right column visually balanced on md+ */}
                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label={'Website'}
                  name={'website'}
                  placeholder={'Website'}
                  fullWidth
                  value={values.website}
                  errorMessage={errors.website}
                  onChange={handleChange('website')}
                />
                <TextField
                  label={'Address'}
                  name={'address'}
                  placeholder={'Address'}
                  fullWidth
                  value={values.address}
                  errorMessage={errors.address}
                  onChange={handleChange('address')}
                />
              </Stack>
            </Stack>

            {submitError && (
              <TextView $fontSize={13} $fontWeight={500} $fontColor="#b91c1c">
                {submitError}
              </TextView>
            )}
          </Stack>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Button
            label={'Cancel'}
            variant={'outlined'}
            onClick={onClose}
            disabled={submitting}
          />
          <Button label={'Save account'} onClick={onSubmit} loading={submitting} />
        </Box>
      </Paper>
    </Modal>
  );
};

export default EditAccountModal;
